import InfoTileContainer from "src/shared-elements/info-tile/info-tile-container.component";
import EquipmentQuantityComponent from "../dashboard-charts/equipment-quantity.component";
import InfoTileComponent from "src/shared-elements/info-tile/info-tile.component";
import { useQuery } from "@tanstack/react-query";
import { getLuminaryStatusByBlockCode } from "src/core/api/services/dashbaorad-details/location-block.services";
import { getHealthStatusByLocationOrAssetCode } from "src/core/api/services/dashboard.services";
import { useEffect, useState } from "react";
import { getAllFaultType } from "src/store/slices/fault-module/fault-report.slice";
import { useDispatch } from "react-redux";

interface DashboardDetailBlockStatusProps {
  blockCode: string | null;
  faultNavigateData?: {
    blockCode: string;
    siteCode: string;
    tcCode: string;
  };
  isLoadingBlockDetails: boolean;
  isRefreshedEquipmentQuantity: boolean;
  isRefreshedLuminaryStatus: boolean;
  isRefreshedHealthStatus: boolean;
}

const DashboardDetailBlockStatus: React.FC<DashboardDetailBlockStatusProps> = ({
  blockCode,
  faultNavigateData,
  isRefreshedEquipmentQuantity,
  isLoadingBlockDetails,
  isRefreshedHealthStatus,
  isRefreshedLuminaryStatus,
}) => {
  const dispatch = useDispatch();
  const [luminaryStatusData, setLuminaryStatusData] = useState<any>();
  const [healthStatusData, setHeathStatus] = useState([] as any);
  const [faultTypes, setFaultTypes] = useState<any>(
    {
      gateway: "",
      light: "",
      network: "",
    },
  );

  const {
    data: luminaryStatusByBlockCode,
    isLoading: isLoadingLuminaryStatusByBlockCode,
  } = useQuery({
    queryKey: ["getLuminaryStatusByBlockCode", blockCode],
    queryFn: ({ signal }) => getLuminaryStatusByBlockCode(blockCode, signal),
    enabled: !!blockCode,
  });

  const {
    data: healthStatusByLocationOrAsset,
    isLoading: isLoadingHealthStatusByLocationOrAsset,
  } = useQuery({
    queryKey: [
      "getHealthStatusByLocationOrAssetCode",
      null,
      null,
      blockCode,
      null,
    ],
    queryFn: ({ signal }) =>
      getHealthStatusByLocationOrAssetCode(null, null, blockCode, null, signal),
    enabled: !!blockCode,
  });

  useEffect(() => {
    if (healthStatusByLocationOrAsset?.data?.data?.length) {
      setHeathStatus(healthStatusByLocationOrAsset?.data?.data[0]);
    }
  }, [healthStatusByLocationOrAsset]);

  useEffect(() => {
    setLuminaryStatusData([
      {
        title: "Burn Time",
        subtitle: "Based on 50,000 hrs",
        content: `${luminaryStatusByBlockCode?.burnTime}%`,
      },

      {
        title: "Energy Saving",
        subtitle: "Last Month",
        content: luminaryStatusByBlockCode?.energySaving === "NA"
        ? "N/A"
        : `${luminaryStatusByBlockCode?.energySaving}%`,
      },
    ]);
  }, [luminaryStatusByBlockCode]);

    useEffect(() => {
      dispatch(getAllFaultType()).then((res: any) => {
        const data = {
          gateway: "",
          light: "",
          network: "",
        };
  
        res?.payload?.data?.forEach((type: any) => {
          if (type?.faultName === "Network") {
            data.network = type?.id;
          } else if (type?.faultName === "Gateway") {
            data.gateway = type?.id;
          } else if (type?.faultName === "Light") {
            data.light = type?.id;
          }
        });
  
        setFaultTypes(data);
      });
    }, []);

  const navigateToFault = (tc: string, site: string, block: string, faultType: string) => {
    const url = new URL(`${window.location.origin}/fault`);
    url.searchParams.append('tc', tc);
    url.searchParams.append('site', site);
    url.searchParams.append('block', block);
    url.searchParams.append('faultType', faultType);
    window.open(url.toString(), '_blank');
  };

  return (
    <div className="w-lg-37">
      <div className="grid-space-between">
        <EquipmentQuantityComponent
          blockCode={blockCode}
          isRefreshedEquipmentQuantity={
            isRefreshedEquipmentQuantity || isLoadingBlockDetails
          }
        />
        <div className="mt-3">
          <InfoTileContainer header="Luminaire Status">
            {luminaryStatusData?.length &&
              luminaryStatusData.map((data: any) => (
                <InfoTileComponent
                  key={data.title}
                  title={data.title}
                  value={data.content}
                  subtitle={data.subtitle}
                  isLoading={
                    isLoadingLuminaryStatusByBlockCode ||
                    isRefreshedLuminaryStatus
                  }
                />
              ))}
          </InfoTileContainer>
        </div>

        <div className="mt-3">
          <InfoTileContainer header="Health Status">
            <InfoTileComponent
              title="Gateway Failure"
              value={healthStatusData?.gatewayFailure}
              className={` ${
                Number(healthStatusData?.gatewayFailure) > 0
                  ? "cursor-pointer link-color"
                  : "text-black "
              }  `}
              onClickEvent={() =>
                Number(healthStatusData?.gatewayFailure) == 0
                  ? null
                  : navigateToFault(
                    faultNavigateData?.tcCode || "",
                    faultNavigateData?.siteCode || "",
                    faultNavigateData?.blockCode || "",
                    faultTypes?.gateway  
                  )
              }
              isLoading={
                isLoadingHealthStatusByLocationOrAsset ||
                isRefreshedHealthStatus
              }
            />
            <InfoTileComponent
              title="Light Failure"
              value={healthStatusData?.lightFailure}
              className={` ${
                Number(healthStatusData?.lightFailure) > 0
                  ? "cursor-pointer link-color"
                  : "text-black "
              }  `}
              onClickEvent={() =>
                Number(healthStatusData?.lightFailure) == 0
                  ? null
                  : navigateToFault(
                    faultNavigateData?.tcCode || "",
                    faultNavigateData?.siteCode || "",
                    faultNavigateData?.blockCode || "",
                    faultTypes?.light  
                  )
              }
              isLoading={
                isLoadingHealthStatusByLocationOrAsset ||
                isRefreshedHealthStatus
              }
            />
            <InfoTileComponent
              title="Sensor Failure"
              value={healthStatusData?.sensorFailure}
              isLoading={
                isLoadingHealthStatusByLocationOrAsset ||
                isRefreshedHealthStatus
              }
            />
            <InfoTileComponent
              title="Network Failure"
              value={healthStatusData?.networkFailure}
              isLoading={
                isLoadingHealthStatusByLocationOrAsset ||
                isRefreshedHealthStatus
              }
              className={` ${
                Number(healthStatusData?.networkFailure) > 0
                  ? "cursor-pointer link-color"
                  : "text-black "
              }  `}
              onClickEvent={() =>
                Number(healthStatusData?.networkFailure) == 0
                  ? null
                  : navigateToFault(
                    faultNavigateData?.tcCode || "",
                    faultNavigateData?.siteCode || "",
                    faultNavigateData?.blockCode || "",
                    faultTypes?.network  
                  )
              }
            />
          </InfoTileContainer>
        </div>
      </div>
    </div>
  );
};

export default DashboardDetailBlockStatus;
